/**
 * Singleton que mantém o schedule compilado em memória e no IndexedDB.
 * Atualizado pelo useScheduleSync. Consumido pelo loop de reprodução.
 */
import type { CompiledSlotDTO, ScheduleCache } from '@/types/player.types'
import { playerLogger } from '@/utils/player-logger'
import { getDB } from './db'

// ─── Estado em memória ───────────────────────────────────────────────────────

let current: ScheduleCache | null = null

// ─── API Pública ─────────────────────────────────────────────────────────────

export const scheduleManager = {
  getCurrent(): ScheduleCache | null {
    return current
  },

  getVersion(): string {
    return current?.version ?? ''
  },

  getCacheAgeSeconds(): number {
    if (!current) return Infinity
    return Math.floor((Date.now() - current.savedAt) / 1000)
  },

  /** Atualiza o schedule em memória e persiste no IndexedDB. */
  async update(slots: CompiledSlotDTO[], version: string): Promise<void> {
    const now = Date.now()
    const cache: ScheduleCache = {
      version,
      compiledAt: new Date().toISOString(),
      slots,
      savedAt: now
    }
    current = cache
    try {
      const db = await getDB()
      await db.put('schedule', { ...cache, key: 'current' }, 'current')
    } catch (err) {
      playerLogger.warn('[schedule-manager] Falha ao persistir no IndexedDB:', err)
    }
  },

  /**
   * Carrega o cache do IndexedDB (restauração após reload).
   * Só aplica se `current` ainda estiver vazio — evita sobrescrever um `update()`
   * mais recente vindo da rede quando as duas chamadas correm em paralelo no mount.
   */
  async loadFromCache(): Promise<ScheduleCache | null> {
    if (current) return current
    try {
      const db = await getDB()
      const cached = await db.get('schedule', 'current')
      if (!cached) return null
      // Cache válido por 24h
      const ageSeconds = (Date.now() - cached.savedAt) / 1000
      if (ageSeconds > 86_400) return null
      current = cached
      return cached
    } catch (err) {
      playerLogger.warn('[schedule-manager] Falha ao carregar cache:', err)
      return null
    }
  },

  clear(): void {
    current = null
  }
}
